import { DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, merge, Observable } from 'rxjs';
import { SessionService } from '../auth/session.service';
import { MessageLevel, ModalDialog, Session } from '../interfaces';
import { toVoid } from '../utils';
import { DialogActionTypes } from './dialogs/dialogs.enum';

@Directive()
export abstract class NotificationState implements ModalDialog {
  protected abstract notificationLevel: MessageLevel;

  public abstract readonly hasUnsavedChanges$: Observable<boolean>;

  /**
   * @dialogActions - Object of nesessary methods to should fire transmuted to btn as (action: string)
   * instance fired at dynamic component within action Handler(action: string)
   */
  protected dialogActions: { [key in DialogActionTypes]: () => void } = {
    closeDialog: () => this.closeDialog(),
    saveRolesChanges: () => this.submit(),
    submit: () => this.submit(),
    submitMessage: () => this.submit(),
    initChatroom: () => this.submit(),
    confirmAction: () => this.confirmAction(),
    resendVerification: () => this.confirmSpecificAction(DialogActionTypes.RESEND_VERIFICATION),
    goToHomepage: () => this.confirmSpecificAction(DialogActionTypes.GO_TO_HOMEPAGE),
    goToLoginPage: () => this.confirmSpecificAction(DialogActionTypes.GO_TO_LOGIN_PAGE),
    goToSecondAuthPage: () => this.confirmSpecificAction(DialogActionTypes.GO_TO_SECOND_AUTH_PAGE),
    goToConnections: () => this.confirmSpecificAction(DialogActionTypes.GO_TO_CONNECTIONS),
    resetConversationTitle: () => this.confirmSpecificAction(DialogActionTypes.RESET_CONVERSATION_TITLE),
    registrationLink: () => this.confirmSpecificAction(DialogActionTypes.GO_TO_REGISTRATION),
    sendReactivationPetition: () => this.confirmSpecificAction(DialogActionTypes.SEND_REACTIVATION_PETITION),
  };

  private router = inject(Router);
  protected destroyRef = inject(DestroyRef);

  // TODO should be private
  public activeModal = inject(NgbActiveModal);

  private sessionEndTrigger$: Observable<void> = inject(SessionService).sessionChanges.pipe(
    filter((session: Session) => !session.token),
    toVoid,
  );

  private navigationTrigger$: Observable<void> = this.router.events.pipe(
    filter((event) => event instanceof NavigationStart && event.navigationTrigger === 'popstate'),
    toVoid,
  );

  public constructor() {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    merge(this.sessionEndTrigger$, this.navigationTrigger$)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.notificationLevel !== 'ERROR') {
          this.closeDialog();
        }
      });
  }

  public onDismiss(): void {
    this.activeModal.dismiss(false);
  }

  public closeDialog(): void {
    this.activeModal.close(false);
  }

  public confirmAction(): void {
    this.activeModal.close(true);
  }

  public confirmSpecificAction(action: string): void {
    this.activeModal.close(action);
  }

  public abstract actionsHandler(action: string): void;

  public abstract submit(data?: string): void;
}
